import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { RecurringEvent } from "./RecurringEventService";
import { Room } from "./RoomService";

// GraphQL queries
const RECURRING_EVENT_ROOM_ASSIGNMENT_LIST_QUERY = gql`
  query RecurringEventRoomAssignmentList(
    $filter: RecurringEventRoomAssignmentFilter
    $params: PaginationAndSorting
  ) {
    recurringEventRoomAssignmentList(filter: $filter, params: $params) {
      code
      data {
        id
        recurringEventId
        roomId
        assignmentType
        notes
        createdAt
        createdBy
        userCreated {
          id
        }
        recurringEvent {
          id
        }
        room {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RECURRING_EVENT_ROOM_ASSIGNMENT_GET_QUERY = gql`
  query RecurringEventRoomAssignmentGet($id: ID) {
    recurringEventRoomAssignmentGet(id: $id) {
      code
      data {
        id
        recurringEventId
        roomId
        assignmentType
        notes
        createdAt
        createdBy
        userCreated {
          id
        }
        recurringEvent {
          id
        }
        room {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RECURRING_EVENT_ROOM_ASSIGNMENT_GET_MANY_QUERY = gql`
  query RecurringEventRoomAssignmentGetMany($ids: [ID]) {
    recurringEventRoomAssignmentGetMany(ids: $ids) {
      code
      data {
        id
        recurringEventId
        roomId
        assignmentType
        notes
        createdAt
        createdBy
        userCreated {
          id
        }
        recurringEvent {
          id
        }
        room {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const RECURRING_EVENT_ROOM_ASSIGNMENT_CREATE_MUTATION = gql`
  mutation RecurringEventRoomAssignmentCreate(
    $params: RecurringEventRoomAssignmentInput
  ) {
    recurringEventRoomAssignmentCreate(params: $params) {
      code
      data {
        id
        recurringEventId
        roomId
        assignmentType
        notes
        createdAt
        createdBy
        userCreated {
          id
        }
        recurringEvent {
          id
        }
        room {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RECURRING_EVENT_ROOM_ASSIGNMENT_UPDATE_MUTATION = gql`
  mutation RecurringEventRoomAssignmentUpdate(
    $where: RecurringEventRoomAssignmentWhereInput
    $params: RecurringEventRoomAssignmentInput
  ) {
    recurringEventRoomAssignmentUpdate(where: $where, params: $params) {
      code
      data {
        id
        recurringEventId
        roomId
        assignmentType
        notes
        createdAt
        createdBy
        userCreated {
          id
        }
        recurringEvent {
          id
        }
        room {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RECURRING_EVENT_ROOM_ASSIGNMENT_REMOVE_MUTATION = gql`
  mutation RecurringEventRoomAssignmentRemove(
    $where: RecurringEventRoomAssignmentWhereInput
  ) {
    recurringEventRoomAssignmentRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RECURRING_EVENT_ROOM_ASSIGNMENT_REMOVE_MANY_MUTATION = gql`
  mutation RecurringEventRoomAssignmentRemoveMany(
    $where: [RecurringEventRoomAssignmentWhereInput]
  ) {
    recurringEventRoomAssignmentRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface RecurringEventRoomAssignment {
  id: string;
  recurringEventId?: string;
  roomId?: string;
  assignmentType?: string;
  notes?: string;
  createdAt?: string;
  createdBy?: string;
  userCreated?: User;
  recurringEvent?: RecurringEvent;
  room?: Room;
}

export interface RecurringEventRoomAssignmentFilter {
  id?: string[];
  recurringEventId?: string[];
  roomId?: string[];
  assignmentType?: string[];
}

export interface RecurringEventRoomAssignmentInput {
  recurringEventId?: string;
  roomId?: string;
  assignmentType?: string;
  notes?: string;
}

export interface RecurringEventRoomAssignmentWhereInput {
  id: string;
}

/**
 * Recurring Event Room Assignment service that handles all recurring event room assignment-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class RecurringEventRoomAssignmentService {
  /**
   * Get a list of recurring event room assignments with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: RecurringEventRoomAssignmentFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      RECURRING_EVENT_ROOM_ASSIGNMENT_LIST_QUERY,
      {
        filter: filter || {},
        params: {
          pagination: pagination || undefined,
          sortBy: sortBy || undefined,
        },
      }
    );
    return result;
  }

  /**
   * Get a single recurring event room assignment by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      RECURRING_EVENT_ROOM_ASSIGNMENT_GET_QUERY,
      {
        id: id,
      }
    );
    return result;
  }

  /**
   * Get multiple recurring event room assignments by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      RECURRING_EVENT_ROOM_ASSIGNMENT_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }

  /**
   * Create a new recurring event room assignment
   */
  static async create(
    params: RecurringEventRoomAssignmentInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      RECURRING_EVENT_ROOM_ASSIGNMENT_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing recurring event room assignment
   */
  static async update(
    id: string,
    params: RecurringEventRoomAssignmentInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      RECURRING_EVENT_ROOM_ASSIGNMENT_UPDATE_MUTATION,
      {
        where: { id },
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a recurring event room assignment
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      RECURRING_EVENT_ROOM_ASSIGNMENT_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple recurring event room assignments
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      RECURRING_EVENT_ROOM_ASSIGNMENT_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
